import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Checkpoint } from '@/graph/domain/types';
import { getCheckpointReferenceFileSignedUrl } from '@/services/editorApi.service';
import { getWorkspaceId } from '@/services/storage.helper';
import { getBridge } from '@/services/communicationBridge.service';
import type { EntityType } from '@/types/communication.types';
import { environment } from '@/config/environment.config';

// const ILA_API_BASE = environment.getIlaApiBaseUrl();
const ILA_BASE = environment.getIlaBaseUrl();

const FIELD_TYPE_COLOR: Record<string, 'primary' | 'success' | 'warning' | 'info' | 'secondary' | 'default'> = {
  text: 'primary',
  textNumber: 'primary',
  number: 'success',
  dropdown: 'warning',
  range: 'info',
  group: 'secondary',
  date: 'info',
  user: 'secondary',
  file: 'default',
  units: 'primary',
  linkEntity: 'default',
  table: 'info',
  gradientProgram: 'info',
};

interface CheckpointListProps {
  checkpoints: Checkpoint[];
  /** 'edit' = compact list with edit/delete buttons, 'preview' = full form view with disabled inputs */
  mode?: 'edit' | 'preview';
  /** When true, edit and delete action buttons are rendered per checkpoint. */
  canEdit?: boolean;
  /** Called when the user clicks the edit icon. Receives the checkpoint index and object. */
  onEdit?: (index: number, checkpoint: Checkpoint) => void;
  /** Called when the user clicks the delete icon. Receives the checkpoint index. */
  onDelete?: (index: number) => void;
  /** Workspace ID - used to construct default storage container for old files without bucket info */
  workspaceId?: string;
}

export function CheckpointList({ checkpoints, mode = 'edit', canEdit = false, onEdit, onDelete, workspaceId }: CheckpointListProps) {
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});
  const communication = {
    sendNavigate: (entityType: EntityType, entityId: string, entityName?: string) =>
      getBridge().sendEvent('NAVIGATE', { entityType, entityId, entityName }),
  };

  if (checkpoints.length === 0) return null;

  const toggleGroupExpand = (checkpointName: string, index: number) => {
    const key = `${checkpointName}_${index}`;
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isGroupExpanded = (checkpointName: string, index: number): boolean => {
    const key = `${checkpointName}_${index}`;
    return expandedGroups[key] || false;
  };

  // EDIT MODE: Compact list view with badges
  if (mode === 'edit') {
    return (
      <Box data-testid="checkpoint-list">
        {/* <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Checkpoints
        </Typography> */}
        <List dense disablePadding>
          {checkpoints.map((cp, index) => {
            const showActions = canEdit || cp.isCustom;
            return (
              <ListItem
                key={cp.id ?? index}
                sx={{
                  py: 0.5,
                  px: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
                data-testid={`checkpoint-${cp.id}`}
              >
                {/* Field-type chip */}
                <Chip
                  label={cp.fieldType}
                  size="small"
                  color={FIELD_TYPE_COLOR[cp.fieldType] ?? 'default'}
                  sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize', flexShrink: 0 }}
                />

                {/* Name + range info */}
                <ListItemText
                  primary={cp.checkpointName}
                  secondary={
                    cp.fieldType === 'range' && cp.rangeMin != null
                      ? `Range: ${cp.rangeMin} – ${cp.rangeMax}`
                      : undefined
                  }
                  slotProps={{
                    primary: { variant: 'body2' },
                    secondary: { variant: 'caption' },
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />

                {/* Required badge */}
                {cp.required && (
                  <Chip
                    label="Required"
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                  />
                )}

                {/* Edit / Delete actions (only when allowed) */}
                {showActions && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {onEdit && (
                      <IconButton
                        size="small"
                        onClick={() => onEdit(index, cp)}
                        title="Edit checkpoint"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(index)}
                        title="Delete checkpoint"
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  }

  // PREVIEW MODE: Full form view with disabled inputs (like workflow-checkpoints)
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Checkpoints:
      </Typography>
      {checkpoints.map((checkpoint, index) => renderCheckpointPreview(checkpoint as any, index, expandedGroups, toggleGroupExpand, isGroupExpanded, workspaceId, communication))}
    </Box>
  );
}

/** Open a checkpoint reference file using dynamic container resolution. */
async function openCheckpointReferenceFile(
  referenceFile: any,
  workspaceId?: string,
): Promise<void> {
  const raw = referenceFile?.raw;
  const blobName = referenceFile?.fileUrl || raw?.filePath || raw?.fileUrl;

  if (!blobName) {
    console.error('[CheckpointList] File path not available', referenceFile);
    return;
  }

  try {
    // Pass blobName only; getCheckpointReferenceFileSignedUrl fetches container dynamically
    const { signedUrl } = await getCheckpointReferenceFileSignedUrl(blobName);
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  } catch (err) {
    console.error('[CheckpointList] Failed to open file:', err);
  }
}

/**
 * Open linked spreadsheet via Angular URL.
 * Run-analysis iframe host currently no-ops NAVIGATE, so direct open is required.
 * Falls back to entityId when versionId is missing.
 */
function openCheckpointSpreadsheet(referenceSpreadsheetFile: any): void {
  if (!referenceSpreadsheetFile) return;

  const versionId =
    referenceSpreadsheetFile.versionId ||
    referenceSpreadsheetFile.entityId ||
    null;
  const name = referenceSpreadsheetFile.name || 'spreadsheet';

  if (!versionId) {
    console.error('[CheckpointList] Spreadsheet version/entity id not available', referenceSpreadsheetFile);
    return;
  }

  const encodedName = encodeURIComponent(encodeURIComponent(name));
  const url = `${ILA_BASE}/folders/SPREADSHEETFILE_VERSION/${encodedName}/${versionId}/edit`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Helper function to render checkbox fields in preview mode
function renderCheckpointPreview(
  checkpoint: any,
  index: number,
  expandedGroups: { [key: string]: boolean },
  toggleGroupExpand: (name: string, idx: number) => void,
  isGroupExpanded: (name: string, idx: number) => boolean,
  workspaceId?: string,
  communication?: any
): React.ReactNode {
  const getRangeDisplay = (cp: any): string => `${cp.rangeMin ?? 'N/A'} - ${cp.rangeMax ?? 'N/A'}`;

  if (checkpoint.fieldType === 'group') {
    return (
      <Box key={`checkpoint-${index}`} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected' }
          }}
          onClick={() => toggleGroupExpand(checkpoint.checkpointName, index)}
        >
          <ChevronRightIcon
            sx={{
              transform: isGroupExpanded(checkpoint.checkpointName, index) ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              mr: 1
            }}
          />
          <Typography sx={{ fontWeight: 400 }}>
            {checkpoint.checkpointName || 'Group'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
        </Box>
        {isGroupExpanded(checkpoint.checkpointName, index) && (
          <Box sx={{ ml: 2, mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
            {checkpoint.children?.map((child: any, childIdx: number) =>
              renderChildFieldPreview(child, childIdx, expandedGroups, toggleGroupExpand, isGroupExpanded, workspaceId, communication)
            )}
          </Box>
        )}
      </Box>
    );
  }

  return renderSimpleFieldPreview(checkpoint, index, workspaceId, communication);
}

// Render simple field in preview mode
function renderSimpleFieldPreview(checkpoint: any, index: number, workspaceId?: string, communication?: any): React.ReactNode {
  const getRangeDisplay = (cp: any): string => `${cp.rangeMin ?? 'N/A'} - ${cp.rangeMax ?? 'N/A'}`;
  const getLinkedEntityText = (value: unknown): string => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || 'No entity linked';
    }
    if (Array.isArray(value)) {
      const labels = value
        .map((v) => {
          if (typeof v === 'string') return v.trim();
          if (v && typeof v === 'object') {
            const obj = v as Record<string, unknown>;
            return String(obj.name ?? obj.label ?? obj._id ?? obj.id ?? '').trim();
          }
          return '';
        })
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : 'No entity linked';
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const label = String(obj.name ?? obj.label ?? obj._id ?? obj.id ?? '').trim();
      return label || 'No entity linked';
    }
    return 'No entity linked';
  };

  switch (checkpoint.fieldType) {
    case 'text':
    case 'textNumber':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem' }}>
            {checkpoint.checkpointName || 'Text'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="text"
            placeholder={`Enter ${checkpoint.checkpointName || 'value'}`}
            value={checkpoint.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 150, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
            slotProps={{
              htmlInput: { pattern: checkpoint.fieldType === 'text' ? '[A-Za-z ]*' : undefined }
            }}
          />
        </Box>
      );

    case 'number':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem' }}>
            {checkpoint.checkpointName || 'Number'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder={`Enter ${checkpoint.checkpointName || 'number'}`}
            value={checkpoint.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 150, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'range':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 130 }}>
            <Typography sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
              {checkpoint.checkpointName || 'Range'}
              {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              ({getRangeDisplay(checkpoint)})
            </Typography>
          </Box>
          <TextField
            size="small"
            type="number"
            placeholder={`Enter ${checkpoint.checkpointName || 'range'}`}
            value={checkpoint.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 150, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'dropdown':
      return (
          <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem' }}>
              {checkpoint.checkpointName || 'Dropdown'}
              {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <FormControl size="small" sx={{ flex: 1, minWidth: 150, maxWidth: 180, backgroundColor: '#f5f5f5', opacity: 0.7 }} disabled>
            <Select value={checkpoint.value || ''} displayEmpty>
              <MenuItem value="" disabled>
                {checkpoint.checkpointName || 'Dropdown'}
              </MenuItem>
              {checkpoint.dropdown?.map((option: any, idx: number) => (
                <MenuItem key={idx} value={option.value || option}>
                  {option.label || option.name || option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );

    case 'units':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem', flexShrink: 0 }}>
            {checkpoint.checkpointName || 'Units'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder="Amount"
            value={checkpoint.value?.amount || ''}
            disabled
            sx={{ flex: 0, minWidth: 80, maxWidth: 100, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
          <FormControl size="small" sx={{ flex: 1, minWidth: 80, maxWidth: 100, backgroundColor: '#f5f5f5', opacity: 0.7 }} disabled>
            <Select value={checkpoint.value?.units || ''}>
              {checkpoint.unitType?.map((unit: string, idx: number) => (
                <MenuItem key={idx} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );

    case 'file':
      return (
          <Box key={`checkpoint-${index}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem', flexShrink: 0 }}>
                {checkpoint.checkpointName || 'File'}
                {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: '0.9rem', color: checkpoint.referenceFile?.fileName ? 'text.primary' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {checkpoint.referenceFile?.fileName || 'No file'}
            </Typography>
              {checkpoint.referenceFile?.fileName && (
                <Button
                  size="small"
                  onClick={() => {
                    void openCheckpointReferenceFile(checkpoint.referenceFile, workspaceId);
                  }}
                >
                  Open
                </Button>
              )}
            </Box>

            {/* Reference Spreadsheet Link - clickable to open spreadsheet */}
            {checkpoint.referenceSpreadsheetFile?.name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 16 }}>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Spreadsheet:</Typography>
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'none' }
                  }}
                  onClick={() => openCheckpointSpreadsheet(checkpoint.referenceSpreadsheetFile)}
                >
                  {checkpoint.referenceSpreadsheetFile.name}
                </Button>
              </Box>
            )}
        </Box>
      );

    case 'user':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem' }}>
            {checkpoint.checkpointName || 'User'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            placeholder={`Enter ${checkpoint.checkpointName || 'user'}`}
            value={checkpoint.value?.name || ''}
            disabled
            sx={{ flex: 1, minWidth: 150, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'date':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem' }}>
            {checkpoint.checkpointName || 'Date'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="datetime-local"
            value={typeof checkpoint.value === 'string' ? checkpoint.value.slice(0, 16) : ''}
            disabled
            sx={{ flex: 1, minWidth: 150, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'linkEntity':
      return (
        <Box key={`checkpoint-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Typography sx={{ minWidth: 130, fontWeight: 400, fontSize: '0.9rem', pt: '6px' }}>
            {checkpoint.checkpointName || 'Link Entity'}
            {checkpoint.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 150, maxWidth: 220 }}>
            {Array.isArray(checkpoint.allowedEntityTypes) && checkpoint.allowedEntityTypes.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                Allowed: {checkpoint.allowedEntityTypes.join(', ')}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {getLinkedEntityText(checkpoint.value)}
            </Typography>
          </Box>
        </Box>
      );

    default:
      return null;
  }
}

// Render child field in preview mode (for group children)
function renderChildFieldPreview(
  child: any,
  childIndex: number,
  expandedGroups: { [key: string]: boolean },
  toggleGroupExpand: (name: string, idx: number) => void,
  isGroupExpanded: (name: string, idx: number) => boolean,
  workspaceId?: string,
  communication?: any
): React.ReactNode {
  const getLinkedEntityText = (value: unknown): string => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || 'No entity linked';
    }
    if (Array.isArray(value)) {
      const labels = value
        .map((v) => {
          if (typeof v === 'string') return v.trim();
          if (v && typeof v === 'object') {
            const obj = v as Record<string, unknown>;
            return String(obj.name ?? obj.label ?? obj._id ?? obj.id ?? '').trim();
          }
          return '';
        })
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : 'No entity linked';
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const label = String(obj.name ?? obj.label ?? obj._id ?? obj.id ?? '').trim();
      return label || 'No entity linked';
    }
    return 'No entity linked';
  };

  if (child.fieldType === 'group') {
    return (
      <Box key={`child-group-${childIndex}`} sx={{ mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => toggleGroupExpand(child.checkpointName, childIndex)}
        >
          <ChevronRightIcon
            sx={{
              transform: isGroupExpanded(child.checkpointName, childIndex) ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              mr: 1
            }}
          />
          <Typography sx={{ fontWeight: 400 }}>
            {child.checkpointName || 'Group'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
        </Box>
        {isGroupExpanded(child.checkpointName, childIndex) && (
          <Box sx={{ ml: 2, mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            {child.children?.map((grandChild: any, gcIdx: number) =>
              renderChildFieldPreview(grandChild, gcIdx, expandedGroups, toggleGroupExpand, isGroupExpanded, workspaceId, communication)
            )}
          </Box>
        )}
      </Box>
    );
  }

  const getRangeDisplay = (cp: any): string => `${cp.rangeMin ?? 'N/A'} - ${cp.rangeMax ?? 'N/A'}`;

  switch (child.fieldType) {
    case 'text':
    case 'textNumber':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem' }}>
            {child.checkpointName || 'Text'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="text"
            placeholder={`Enter ${child.checkpointName || 'value'}`}
            value={child.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 120, maxWidth: 150, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
            slotProps={{
              htmlInput: { pattern: child.fieldType === 'text' ? '[A-Za-z ]*' : undefined }
            }}
          />
        </Box>
      );

    case 'number':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem' }}>
            {child.checkpointName || 'Number'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder={`Enter ${child.checkpointName || 'number'}`}
            value={child.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 120, maxWidth: 150, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'range':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
            <Typography sx={{ fontWeight: 400, fontSize: '0.85rem' }}>
              {child.checkpointName || 'Range'}
              {child.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              ({getRangeDisplay(child)})
            </Typography>
          </Box>
          <TextField
            size="small"
            type="number"
            placeholder={`Enter ${child.checkpointName || 'range'}`}
            value={child.value || ''}
            disabled
            sx={{ flex: 1, minWidth: 120, maxWidth: 150, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'dropdown':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem' }}>
            {child.checkpointName || 'Dropdown'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <FormControl size="small" sx={{ flex: 1, minWidth: 120, maxWidth: 150, backgroundColor: '#f5f5f5', opacity: 0.7 }} disabled>
            <Select value={child.value || ''} displayEmpty>
              <MenuItem value="" disabled>
                {child.checkpointName || 'Dropdown'}
              </MenuItem>
              {child.dropdown?.map((option: any, idx: number) => (
                <MenuItem key={idx} value={option.value || option}>
                  {option.label || option.name || option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );

    case 'units':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem', flexShrink: 0 }}>
            {child.checkpointName || 'Units'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder="Amount"
            value={child.value?.amount || ''}
            disabled
            sx={{ flex: 0, minWidth: 70, maxWidth: 90 }}
          />
          <FormControl size="small" sx={{ flex: 1, minWidth: 70, maxWidth: 90 }} disabled>
            <Select value={child.value?.units || ''}>
              {child.unitType?.map((unit: string, idx: number) => (
                <MenuItem key={idx} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );

    case 'file':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem', flexShrink: 0 }}>
              {child.checkpointName || 'File'}
              {child.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: child.referenceFile?.fileName ? 'text.primary' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
              {child.referenceFile?.fileName || 'No file'}
            </Typography>
            {child.referenceFile?.fileName && (
              <Button
                size="small"
                onClick={() => {
                  void openCheckpointReferenceFile(child.referenceFile, workspaceId);
                }}
              >
                Open
              </Button>
            )}
          </Box>
          {child.referenceSpreadsheetFile?.name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 15 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Spreadsheet:</Typography>
              <Button
                size="small"
                variant="text"
                sx={{
                  textTransform: 'none',
                  p: 0,
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'none' },
                }}
                onClick={() => openCheckpointSpreadsheet(child.referenceSpreadsheetFile)}
              >
                {child.referenceSpreadsheetFile.name}
              </Button>
            </Box>
          )}
        </Box>
      );

    case 'user':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem' }}>
            {child.checkpointName || 'User'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            placeholder={`Enter ${child.checkpointName || 'user'}`}
            value={child.value?.name || ''}
            disabled
            sx={{ flex: 1, minWidth: 120, maxWidth: 150, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'date':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem' }}>
            {child.checkpointName || 'Date'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            type="datetime-local"
            value={typeof child.value === 'string' ? child.value.slice(0, 16) : ''}
            disabled
            sx={{ flex: 1, minWidth: 120, maxWidth: 180, '& .MuiOutlinedInput-input': { padding: '6.5px 14px' }, backgroundColor: '#f5f5f5', opacity: 0.7 }}
          />
        </Box>
      );

    case 'linkEntity':
      return (
        <Box key={`child-${childIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 0.5 }}>
          <Typography sx={{ minWidth: 120, fontWeight: 400, fontSize: '0.85rem', pt: '6px' }}>
            {child.checkpointName || 'Link Entity'}
            {child.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 120, maxWidth: 180 }}>
            {Array.isArray(child.allowedEntityTypes) && child.allowedEntityTypes.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25, fontSize: '0.7rem' }}>
                Allowed: {child.allowedEntityTypes.join(', ')}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              {getLinkedEntityText(child.value)}
            </Typography>
          </Box>
        </Box>
      );

    default:
      return null;
  }
}
